import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'
import { classifyOtpInput, isOtpSubmittable } from '../utils/twoFactor'

export function LoginPage() {
  const { t } = useTranslation()
  const { login, loginTotp, register } = useAuth()
  const navigate = useNavigate()
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // 2FA challenge state — после успешной проверки пароля
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [totpCode, setTotpCode] = useState('')
  const [trustDevice, setTrustDevice] = useState(false)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (mode === 'login') {
        const result = await login(email, password)
        if (result.requireTotp) {
          setChallengeToken(result.challengeToken)
          return
        }
      } else {
        if (displayName.trim().length < 2) throw new Error(t('loginPage.fioRequired'))
        await register(email, password, displayName.trim())
      }
      navigate('/app')
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.status === 401
            ? t('loginPage.wrongCredentials')
            : err.message
          : err instanceof Error
          ? err.message
          : t('loginPage.unknownError')
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  const onSubmitTotp = async (e: FormEvent) => {
    e.preventDefault()
    if (!challengeToken) return
    setError(null)
    setBusy(true)
    try {
      await loginTotp(challengeToken, classifyOtpInput(totpCode).value, trustDevice)
      navigate('/app')
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.status === 401
            ? t('loginPage.wrongTotpCode')
            : err.status === 423
            ? t('loginPage.totpLocked')
            : err.message
          : err instanceof Error
          ? err.message
          : t('loginPage.unknownError')
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  const cancelTotp = () => {
    setChallengeToken(null)
    setTotpCode('')
    setTrustDevice(false)
    setError(null)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-soft to-white p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <img src="/edpad-logo.png" alt="EdPad" className="w-10 h-10 rounded-lg" />
          <div>
            <h1 className="text-xl font-semibold">EdPad Учитель</h1>
            <p className="text-sm text-gray-500">{t('loginPage.webVersion')}</p>
          </div>
        </div>

        {challengeToken ? (
          // ── Шаг 2: TOTP ────────────────────────────────────────────────
          <form onSubmit={onSubmitTotp} className="space-y-4">
            <div>
              <h2 className="font-semibold mb-1">{t('loginPage.twoFactorTitle')}</h2>
              <p className="text-sm text-gray-600">
                {t('loginPage.totpHint')}
              </p>
            </div>
            <div>
              <input
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder={t('loginPage.totpPlaceholder')}
                className="input-field text-center text-lg tracking-widest"
                required
                autoFocus
                autoComplete="one-time-code"
                aria-label={t('loginPage.twoFactorTitle')}
              />
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                {t('loginPage.trustDevice')}
                <span className="block text-xs text-gray-500">{t('loginPage.trustDeviceHint')}</span>
              </span>
            </label>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}
            <button type="submit" disabled={busy || !isOtpSubmittable(totpCode)} className="btn-primary w-full">
              {busy ? t('loginPage.checking') : t('common.confirm')}
            </button>
            <button
              type="button"
              onClick={cancelTotp}
              className="text-sm text-gray-500 hover:text-primary w-full text-center"
            >
              ← {t('loginPage.useAnotherAccount')}
            </button>
          </form>
        ) : (
          // ── Шаг 1: email/пароль ────────────────────────────────────────
          <>
            <div className="flex gap-1 mb-6 bg-gray-100 rounded-md p-1">
              <button
                type="button"
                className={`flex-1 py-2 rounded text-sm font-medium ${
                  mode === 'login' ? 'bg-white shadow text-primary' : 'text-gray-600'
                }`}
                onClick={() => setMode('login')}
              >
                {t('common.login')}
              </button>
              <button
                type="button"
                className={`flex-1 py-2 rounded text-sm font-medium ${
                  mode === 'register' ? 'bg-white shadow text-primary' : 'text-gray-600'
                }`}
                onClick={() => setMode('register')}
              >
                {t('loginPage.registerTab')}
              </button>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium mb-1">{t('loginPage.fioLabel')}</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={t('loginPage.fioPlaceholder')}
                    className="input-field"
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">{t('loginPage.emailLabel')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('loginPage.emailPlaceholder')}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('loginPage.passwordLabel')}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('loginPage.passwordPlaceholder')}
                  className="input-field"
                  required
                  minLength={8}
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {error}
                </div>
              )}

              <button type="submit" disabled={busy} className="btn-primary w-full">
                {busy ? t('loginPage.pleaseWait') : mode === 'login' ? t('common.login') : t('loginPage.createAccount')}
              </button>
            </form>
          </>
        )}

        <p className="text-xs text-gray-500 mt-6 text-center">
          {t('loginPage.footerNote')}
        </p>
      </div>
    </div>
  )
}
